import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { createResourceLocatorString } from '../util/routes';
import {
  getFavoriteListingIds,
  normalizeFavoriteListingIds,
  toggleFavoriteId,
} from '../util/favorites';
import { currentUserSelector, setCurrentUser, updateCurrentUserProfile } from '../ducks/user.duck';
import { isAuthenticatedSelector } from '../ducks/auth.duck';

const updateCurrentUserFavorites = (currentUser, favoriteListingIds) => {
  if (!currentUser) return null;
  const profile = currentUser.attributes?.profile || {};
  const privateData = profile.privateData || {};
  return {
    ...currentUser,
    attributes: {
      ...currentUser.attributes,
      profile: {
        ...profile,
        privateData: {
          ...privateData,
          favoriteListingIds,
        },
      },
    },
  };
};

export const useFavoriteListings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const currentUser = useSelector(currentUserSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const favoriteIds = useMemo(() => getFavoriteListingIds(currentUser), [currentUser]);

  const redirectToLogin = useCallback(() => {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };
    history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
  }, [history, location.hash, location.pathname, location.search, routeConfiguration]);

  const setFavorites = useCallback(
    async listingIds => {
      if (!isAuthenticated) {
        redirectToLogin();
        return;
      }
      if (!currentUser) return;

      const nextIds = normalizeFavoriteListingIds(listingIds);
      const privateData = currentUser.attributes?.profile?.privateData || {};
      const optimisticUser = updateCurrentUserFavorites(currentUser, nextIds);

      dispatch(setCurrentUser(optimisticUser));

      try {
        await dispatch(
          updateCurrentUserProfile({
            privateData: {
              ...privateData,
              favoriteListingIds: nextIds,
            },
          })
        );
      } catch (e) {
        dispatch(setCurrentUser(updateCurrentUserFavorites(currentUser, favoriteIds)));
      }
    },
    [currentUser, dispatch, favoriteIds, isAuthenticated, redirectToLogin]
  );

  const toggleFavorite = useCallback(
    async listingId => {
      if (!isAuthenticated) {
        redirectToLogin();
        return;
      }
      if (!currentUser || !listingId) return;

      const privateData = currentUser.attributes?.profile?.privateData || {};
      const nextIds = toggleFavoriteId(favoriteIds, listingId);
      const optimisticUser = updateCurrentUserFavorites(currentUser, nextIds);

      dispatch(setCurrentUser(optimisticUser));

      try {
        await dispatch(
          updateCurrentUserProfile({
            privateData: {
              ...privateData,
              favoriteListingIds: nextIds,
            },
          })
        );
      } catch (e) {
        dispatch(setCurrentUser(updateCurrentUserFavorites(currentUser, favoriteIds)));
      }
    },
    [currentUser, dispatch, favoriteIds, isAuthenticated, redirectToLogin]
  );

  const isFavorited = useCallback(
    listingId => {
      if (!listingId) return false;
      return favoriteIds.includes(listingId);
    },
    [favoriteIds]
  );

  return {
    favoriteIds,
    isFavorited,
    toggleFavorite,
    setFavorites,
  };
};

export default useFavoriteListings;
